import type { LocaleCode } from '~/constants/i18n';
import { getLocaleMeta } from './locale';

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;
const MONTH_MS = 30 * DAY_MS;
const YEAR_MS = 365 * DAY_MS;

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', YEAR_MS],
  ['month', MONTH_MS],
  ['week', WEEK_MS],
  ['day', DAY_MS],
  ['hour', HOUR_MS],
  ['minute', MINUTE_MS],
  ['second', SECOND_MS],
];

const CURRENCY_FRACTION_DIGITS = 0;

const tag = (locale: LocaleCode) => `${getLocaleMeta(locale).language}-u-nu-latn`;

export const formatNumber = (value: number, locale: LocaleCode, options?: Intl.NumberFormatOptions) =>
  new Intl.NumberFormat(tag(locale), options).format(value);

export const formatCurrency = (
  value: number,
  currency: string,
  locale: LocaleCode,
  options?: Intl.NumberFormatOptions,
) =>
  new Intl.NumberFormat(tag(locale), {
    style: 'currency',
    currency,
    maximumFractionDigits: CURRENCY_FRACTION_DIGITS,
    ...options,
  }).format(value);

export const formatDate = (value: Date | string | number, locale: LocaleCode, options?: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(tag(locale), options ?? { dateStyle: 'medium' }).format(new Date(value));

export const formatRelativeTime = (value: Date | string | number, locale: LocaleCode, now: Date = new Date()) => {
  const diff = new Date(value).getTime() - now.getTime();
  const [unit, ms] = RELATIVE_UNITS.find(([, size]) => Math.abs(diff) >= size) ?? ['second', SECOND_MS];
  return new Intl.RelativeTimeFormat(tag(locale), { numeric: 'auto' }).format(Math.round(diff / ms), unit);
};
